import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { SearchContractsResult } from "@/lib/coach-manager/client";
import type { Contract } from "@/lib/coach-manager/types";
import { contractSearchKeys } from "@/lib/query";
import type { ContractSearchRequest } from "@/schemas/contracts";

async function extractErrorMessage(res: Response, fallback: string): Promise<string> {
  const json = await res.json().catch(() => null);
  return json?.error ?? fallback;
}

/**
 * The current user's persisted last Contract Search — one row per user (see
 * `contract_search_state`), superseded on every new search attempt regardless
 * of outcome.
 */
export interface ContractSearchState {
  criteria: ContractSearchRequest;
  status: "success" | "error";
  contracts: Contract[] | null;
  errorMessage: string | null;
  searchedAt: string;
}

/**
 * Search Coach Manager for Contracts via a saved BU instance. Mirrors
 * `useTestConnection`'s shape — a `useMutation` with no cache invalidation
 * (read-only probe, not a write). Never throws on a Coach-Manager-level
 * failure (`{ ok: false, error }` is a valid result the caller renders
 * inline); only throws on a transport/validation failure of the route itself
 * (401/400/404).
 */
export function useSearchContracts() {
  return useMutation({
    mutationFn: async (input: ContractSearchRequest): Promise<SearchContractsResult> => {
      const res = await fetch("/api/coach-manager/contracts/search", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, "Contract search failed"));
      return res.json();
    },
  });
}

/** The persisted last Contract Search for the current user, or `null` if none. */
export function useLastContractSearch() {
  return useQuery({
    queryKey: contractSearchKeys.last,
    queryFn: async (): Promise<ContractSearchState | null> => {
      const res = await fetch("/api/coach-manager/contracts/search/last");
      if (!res.ok) throw new Error(await extractErrorMessage(res, "Failed to load last search"));
      const json = await res.json();
      return json.state;
    },
  });
}

/** Clears the persisted last Contract Search for the current user. */
export function useClearLastContractSearch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/coach-manager/contracts/search/last", { method: "DELETE" });
      if (!res.ok) throw new Error(await extractErrorMessage(res, "Failed to clear search"));
    },
    onSuccess: () => {
      queryClient.setQueryData(contractSearchKeys.last, null);
    },
  });
}
